import { CardMedia, Divider, Stack, Typography } from '@mui/material';
import posthog from 'posthog-js';
import { useEffect, useMemo } from 'react';

import { NewsModel } from '@portal-clients/news';

import { useSiteUnitsQuery } from '@frontend/data';
import { useLocale } from '@frontend/hooks';
import { tApp } from '@frontend/locales';

import { MarkdownDisplay } from '../markdown-display.component';

export interface NewsDrawerContentProps {
  news: NewsModel;
}

export const NewsDrawerContent = (props: NewsDrawerContentProps) => {
  const { key, formatters } = useLocale();
  const { data: siteUnits } = useSiteUnitsQuery();

  const siteUnitNames = useMemo(() => {
    if (!props.news?.siteUnitIds || props.news.siteUnitIds.length === 0) {
      return;
    }

    const externalNames = props.news.siteUnitIds
      .map(
        (id) => siteUnits?.find((siteUnit) => siteUnit.id === id)?.externalName,
      )
      .filter((siteUnit) => siteUnit);

    if (externalNames.length === 0) {
      return;
    }

    return externalNames.join(', ');
  }, [siteUnits]);

  const publishDate = useMemo(() => {
    const date = new Date(props.news.publishDate);
    return formatters.relativeDatetime(date);
  }, [props.news.publishDate, key]);

  useEffect(() => {
    if (!props.news?.id) {
      return;
    }

    posthog.capture('news_article_viewed', {
      data_id: props.news.id,
      scope_ids: props.news.siteUnitIds,
    });
  }, [props.news?.id]);

  return (
    <Stack direction="column" spacing={2}>
      {(props.news.image || props.news.imageUrl) && (
        <CardMedia
          sx={{ aspectRatio: '3/2', objectFit: 'cover', borderRadius: '8px' }}
          role="presentation"
          component="img"
          alt={props.news.image?.altText}
          loading="lazy"
          image={
            props.news.image?.imageUrl
              ? props.news.image.imageUrl
              : props.news.imageUrl
          }
        />
      )}

      <Stack spacing={0.5}>
        <Typography variant="subtitle1">{props.news.title}</Typography>
        <MarkdownDisplay markdown={props.news.bodyText} />
      </Stack>

      <Divider />

      <Stack spacing={0.5}>
        <Typography variant="body2" color="textSecondary">
          {publishDate}
        </Typography>
        {siteUnitNames && (
          <Typography variant="body2" color="textSecondary">
            {tApp.headers.siteUnits()}: {siteUnitNames}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};
